/**
 * HowItWorks — 3-step process section for the customer landing page.
 */

import COLORS from "../config/colors";

const C = COLORS;

const STEPS = [
  {
    number: "01",
    title: "Answer a Few Questions Online",
    description:
      "Tell us your project size, preferred materials, and any add-ons you want. Upload photos of your space. Takes about 2 minutes and there's zero commitment.",
    icon: (
      <svg width="40" height="40" viewBox="0 0 48 48" fill="none" stroke={C.honey} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="8" y="6" width="32" height="36" rx="4"/>
        <line x1="16" y1="16" x2="32" y2="16"/>
        <line x1="16" y1="22" x2="32" y2="22"/>
        <line x1="16" y1="28" x2="24" y2="28"/>
        <circle cx="34" cy="38" r="7" fill={C.honey} fillOpacity="0.15" stroke={C.honey}/>
        <polyline points="31,38 33,40 37,36"/>
      </svg>
    ),
    color: C.honey,
  },
  {
    number: "02",
    title: "Choose Your Materials & Options",
    description:
      "Our AI assistant walks you through material grades, finish options, and climate-appropriate specs for your area. See a real price — not a range — before we ever show up.",
    icon: (
      <svg width="40" height="40" viewBox="0 0 48 48" fill="none" stroke={C.pine} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="12" width="16" height="10" rx="3" fill={C.pine} fillOpacity="0.12"/>
        <rect x="6" y="12" width="16" height="10" rx="3"/>
        <rect x="26" y="12" width="16" height="10" rx="3" fill={C.pine} fillOpacity="0.12"/>
        <rect x="26" y="12" width="16" height="10" rx="3"/>
        <rect x="6" y="28" width="16" height="10" rx="3"/>
        <rect x="26" y="28" width="16" height="10" rx="3" fill={C.pine} fillOpacity="0.30"/>
        <rect x="26" y="28" width="16" height="10" rx="3"/>
        <polyline stroke={C.white} strokeWidth="2.5" points="29,33 32,36 38,30"/>
      </svg>
    ),
    color: C.pine,
  },
  {
    number: "03",
    title: "We Build It — Fixed Price, No Surprises",
    description:
      "Your estimate is your price. We pull permits, handle inspections, and complete the job on schedule. If something unexpected comes up on our end, it's on us — not you.",
    icon: (
      <svg width="40" height="40" viewBox="0 0 48 48" fill="none" stroke="#C4913B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 24L8 18l4-4 2 2 8-8 4 4-12 12z" fill="#C4913B" fillOpacity="0.15"/>
        <path d="M14 24L8 18l4-4 2 2 8-8 4 4-12 12z"/>
        <path d="M28 36L22 30l4-4 2 2 8-8 4 4-12 12z" fill="#C4913B" fillOpacity="0.15"/>
        <path d="M28 36L22 30l4-4 2 2 8-8 4 4-12 12z"/>
      </svg>
    ),
    color: "#C4913B",
  },
];

export default function HowItWorks({ onCTAClick }) {
  return (
    <section
      style={{
        padding: "64px 0 72px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Section header */}
      <div style={{ textAlign: "center", marginBottom: 52 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 3, color: C.honey, marginBottom: 12 }}>
          The Process
        </div>
        <h2
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(26px, 4vw, 42px)",
            fontWeight: 800,
            color: C.bark,
            margin: "0 0 16px",
            lineHeight: 1.15,
          }}
        >
          How It Works
        </h2>
        <p style={{ fontSize: 16, color: C.stone, maxWidth: 520, margin: "0 auto", lineHeight: 1.65 }}>
          From first click to final inspection — a transparent, no-surprise process built around your schedule.
        </p>
      </div>

      {/* Steps */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 24,
          position: "relative",
        }}
      >
        {STEPS.map((step, idx) => (
          <div
            key={step.number}
            style={{
              background: C.white,
              borderRadius: 18,
              padding: "36px 28px 32px",
              boxShadow: "0 1px 6px rgba(44,24,16,0.06)",
              border: "1px solid rgba(155,142,130,0.14)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Step number watermark */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -8,
                right: 16,
                fontFamily: "'Playfair Display', serif",
                fontSize: 96,
                fontWeight: 800,
                color: `${step.color}0A`,
                lineHeight: 1,
                userSelect: "none",
              }}
            >
              {step.number}
            </div>

            {/* Icon */}
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 16,
                background: `${step.color}10`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 22,
              }}
            >
              {step.icon}
            </div>

            {/* Step number label */}
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 2.5,
                color: step.color,
                marginBottom: 10,
              }}
            >
              Step {idx + 1}
            </div>

            {/* Title */}
            <h3
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 20,
                fontWeight: 700,
                color: C.bark,
                margin: "0 0 14px",
                lineHeight: 1.3,
              }}
            >
              {step.title}
            </h3>

            {/* Description */}
            <p style={{ fontSize: 14, color: C.stone, lineHeight: 1.7, margin: 0 }}>
              {step.description}
            </p>
          </div>
        ))}
      </div>

      {/* Bottom CTA strip */}
      <div
        className="hiw-cta-strip"
        style={{
          marginTop: 48,
          padding: "36px 40px",
          borderRadius: 18,
          background: `linear-gradient(135deg, ${C.bark} 0%, #1a0f08 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
      <style>{`
        @media (max-width: 560px) {
          .hiw-cta-strip { padding: 28px 24px !important; flex-direction: column; text-align: center; }
          .hiw-cta-strip button { width: 100% !important; }
        }
      `}</style>
        <div>
          <div
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(20px, 2.5vw, 28px)",
              fontWeight: 700,
              color: C.white,
              marginBottom: 8,
            }}
          >
            Ready to see your number?
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.60)", maxWidth: 420 }}>
            No salespeople. No site visits required to get your estimate. Just a real price you can count on.
          </div>
        </div>

        <button
          onClick={onCTAClick}
          style={{
            padding: "16px 40px",
            borderRadius: 12,
            border: "none",
            background: C.honey,
            color: C.white,
            fontSize: 16,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: 0.3,
            boxShadow: "0 4px 24px rgba(196,145,59,0.45)",
            whiteSpace: "nowrap",
            flexShrink: 0,
            transition: "transform 0.15s, box-shadow 0.15s",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 8px 32px rgba(196,145,59,0.55)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = "";
            e.currentTarget.style.boxShadow = "0 4px 24px rgba(196,145,59,0.45)";
          }}
        >
          Get Free Estimate →
        </button>
      </div>
    </section>
  );
}
